import { useEffect, useState } from "react";
import clsx from "clsx";
import { useDuelStore, formatCountdown } from "../store/useDuelStore";
import { useGameStore } from "../store/useGameStore";
import { CLASS_LABEL } from "../utils/assets";
import { energyCostLabel, hasEnergyFor } from "../utils/assets";
import { FormattedNumber } from "../components/FormattedNumber";

export function DuelsScreen() {
  const { hero, setScreen } = useGameStore();
  const {
    myRoom,
    rooms,
    loading,
    error,
    createRoom,
    joinRoom,
    leaveRoom,
    refreshRooms,
    clearMyRoom,
  } = useDuelStore();

  const [countdown, setCountdown] = useState(myRoom?.seconds_remaining ?? 0);

  useEffect(() => {
    void refreshRooms();
    const listTimer = setInterval(() => void refreshRooms(), 5000);
    return () => clearInterval(listTimer);
  }, [refreshRooms]);

  useEffect(() => {
    setCountdown(myRoom?.seconds_remaining ?? 0);
  }, [myRoom?.seconds_remaining]);

  useEffect(() => {
    if (!myRoom || myRoom.status !== "waiting") return;
    const t = setInterval(() => {
      setCountdown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [myRoom]);

  const canCreate = hasEnergyFor(hero, "PVP");

  if (myRoom?.status === "waiting") {
    return (
      <div className="p-4 space-y-4">
        <button
          type="button"
          onClick={() => setScreen("main")}
          className="text-sm text-gray-400 hover:text-white"
        >
          ← Back to Home
        </button>

        <div className="text-center space-y-2">
          <div className="text-5xl">🤺</div>
          <h2 className="text-xl font-bold font-game text-gold-400">Waiting for opponent</h2>
          <p className="text-gray-400 text-sm">
            Your room is open. You can switch tabs — the battle starts automatically when someone joins.
          </p>
        </div>

        <div className="bg-kingdom-purple border border-kingdom-glow rounded-xl p-5 text-center space-y-3">
          <div className="text-gray-400 text-xs uppercase tracking-wide">Time left</div>
          <div className="text-4xl font-black text-gold-400 tabular-nums">{formatCountdown(countdown)}</div>
          <div className="text-sm text-gray-300">
            {hero?.name ?? "You"} · BR <FormattedNumber value={myRoom.host_br} />
          </div>
        </div>

        <button
          type="button"
          onClick={() => void leaveRoom()}
          disabled={loading}
          className="w-full py-3 rounded-xl border border-rose-500/50 text-rose-300 font-semibold active:scale-95 transition-transform disabled:opacity-50"
        >
          {loading ? "…" : "Cancel room"}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <button
        type="button"
        onClick={() => {
          clearMyRoom();
          setScreen("main");
        }}
        className="text-sm text-gray-400 hover:text-white"
      >
        ← Back to Home
      </button>

      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold font-game text-gold-400">🤺 Duels</h2>
        <p className="text-gray-400 text-sm">PvP 1×1 · lower BR strikes first</p>
      </div>

      <button
        type="button"
        onClick={() => void createRoom()}
        disabled={loading || !canCreate}
        className={clsx(
          "w-full py-4 rounded-xl font-bold text-lg active:scale-95 transition-all",
          canCreate
            ? "bg-gradient-to-r from-rose-600 to-red-700 text-white shadow-lg"
            : "bg-kingdom-mid text-gray-500 cursor-not-allowed",
        )}
      >
        {loading ? "…" : `Create room · ${energyCostLabel(hero, "PVP") || "⚡ 12"}`}
      </button>

      {!canCreate && (
        <p className="text-amber-300/90 text-xs text-center">Not enough energy for a duel</p>
      )}

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Open rooms</h3>
        {rooms.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-6">No open rooms yet</p>
        ) : (
          <div className="space-y-2">
            {rooms.map((room) => (
              <DuelRoomCard
                key={room.room_id}
                room={room}
                disabled={loading || !canCreate}
                onJoin={() => void joinRoom(room.room_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DuelRoomCard({
  room,
  disabled,
  onJoin,
}: {
  room: {
    host_name: string;
    host_br: number;
    host_class: string;
    host_level: number;
    seconds_remaining: number;
  };
  disabled: boolean;
  onJoin: () => void;
}) {
  return (
    <div className="flex items-center gap-3 bg-kingdom-purple border border-kingdom-mid rounded-xl p-3">
      <div className="text-2xl shrink-0">⚔️</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white truncate">{room.host_name}</div>
        <div className="text-xs text-gray-400">
          Lv.{room.host_level} · {CLASS_LABEL[room.host_class] ?? room.host_class} · BR{" "}
          <FormattedNumber value={room.host_br} />
        </div>
        <div className="text-xs text-amber-300/80">{formatCountdown(room.seconds_remaining)}</div>
      </div>
      <button
        type="button"
        onClick={onJoin}
        disabled={disabled}
        className="shrink-0 px-4 py-2 rounded-lg bg-kingdom-glow/20 border border-kingdom-glow text-kingdom-glow text-sm font-bold active:scale-95 disabled:opacity-40"
      >
        Join
      </button>
    </div>
  );
}
